import ExcelJS from '@zurmokeeper/exceljs'

function cellToValue(cell, { raw = false, defval = '' } = {}) {
  if (!cell || cell.value == null || cell.value === '') {
    return defval
  }

  const { value } = cell

  // ExcelJS rich text / formula / hyperlink shapes
  if (typeof value === 'object') {
    if (value.result != null) {
      return raw ? value.result : String(value.result)
    }
    if (value.richText) {
      const text = value.richText.map((part) => part.text).join('')
      return raw ? text : text
    }
    if (value.text != null) {
      return raw ? value.text : String(value.text)
    }
    if (value instanceof Date) {
      return raw ? value : value.toISOString()
    }
  }

  if (value instanceof Date) {
    return raw ? value : value.toISOString()
  }

  return raw ? value : String(value)
}

function rowToArray(row, columnCount, options) {
  const values = new Array(columnCount).fill(options.defval ?? '')
  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    values[colNumber - 1] = cellToValue(cell, options)
  })
  return values
}

function getColumnCount(worksheet) {
  let max = worksheet.columnCount || 0
  worksheet.eachRow({ includeEmpty: true }, (row) => {
    max = Math.max(max, row.cellCount || 0)
  })
  return max
}

/**
 * Load an .xlsx ArrayBuffer into an ExcelJS workbook.
 * Legacy .xls is not supported — callers should accept .xlsx (+ CSV separately).
 */
export async function loadWorkbook(data) {
  const workbook = new ExcelJS.Workbook()
  await workbook.xlsx.load(data)
  return workbook
}

/**
 * SheetJS-compatible: array of objects keyed by header row.
 */
export function sheetToJson(worksheet, { defval = '', raw = false } = {}) {
  const columnCount = getColumnCount(worksheet)
  const rows = []
  let headers = null

  worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    const values = rowToArray(row, columnCount, { defval, raw  })
    if (rowNumber === 1) {
      headers = values.map((h) => (h == null || h === '' ? '' : String(h)))
      return
    }
    if (!headers) return
    const obj = {}
    headers.forEach((header, index) => {
      if (!header) return
      obj[header] =
        values[index] === undefined || values[index] === ''
          ? defval
          : values[index]
    })
    rows.push(obj)
  })

  return rows
}

/**
 * SheetJS sheet_to_json(..., { header: 1  }) — array of arrays.
 */
export function sheetToAoA(worksheet, { defval = '', raw = false } = {}) {
  const columnCount = getColumnCount(worksheet)
  const rows = []
  worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    while (rows.length < rowNumber - 1) {
      rows.push(new Array(columnCount).fill(defval))
    }
    rows[rowNumber - 1] = rowToArray(row, columnCount, { defval, raw  })
  })
  return rows
}

function escapeCsvCell(value) {
  const str = value == null ? '' : String(value)
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str
}

/**
 * SheetJS sheet_to_csv equivalent for the first/given worksheet.
 */
export function sheetToCsv(worksheet, { defval = '', raw = false } = {}) {
  return sheetToAoA(worksheet, { defval, raw  })
    .map((row) => row.map(escapeCsvCell).join(','))
    .join('\n')
}

/**
 * Parse an .xlsx file into { name, rows }[] with object rows (header keys).
 */
export async function parseExcelToSheets(fileOrBuffer) {
  let buffer = fileOrBuffer
  if (!(fileOrBuffer instanceof ArrayBuffer) && !ArrayBuffer.isView(fileOrBuffer)) {
    if (typeof fileOrBuffer?.arrayBuffer === 'function') {
      buffer = await fileOrBuffer.arrayBuffer()
    }
  }
  const workbook = await loadWorkbook(buffer)
  return workbook.worksheets.map((worksheet) => ({
    name: worksheet.name,
    rows: sheetToJson(worksheet, { defval: '', raw: false  }),
    worksheet,
  }))
}
